import { createSelector } from 'reselect'
import { isEmpty, map, keyBy, filter, reduce, values, each, find } from 'lodash'
import { PullDirectionType } from '@Admin/AdminQuotingPage/ModelViewerPanel/PanelControls/PullDirectionMenu'

import { quaternionToMatrix, matrixToQuaternionArray } from './rotationHelpers'
import {
    ACTIVE_COLOR,
    ALERT_COLOR,
    ANNOTATION_MODE,
    CROSS_SECTION_ANNOTATION_MODE,
    CROSS_SECTION_MODE,
    DEFAULT_COLOR,
    DEFAULT_STATE,
    NEW_COMMENT_ID,
    PIN_COMMENT,
    PULL_DIRECTION_MAP,
    SELECT_MODE,
    SURFACE_COMMENT,
    TAPS_INSPECTION_MODE,
    TEXTURED_FEATURES,
    TEXTURED_FEATURES_WITH_DIRECTION,
    TEXTURED_FEATURES_WITH_THRESHOLD,
    XSECTION_COMMENT,
} from './constants'
import {
    ViewerStateType,
    DfmSnapshotType,
    AnnotationType,
    PatchConfigType,
    DfmFeatureConfigType,
    CameraConfigType,
    CrossSectionStateConfigType,
    CommentType,
    TripleFloatType,
} from './types'

import { NAMESPACE } from './index'

type QMRConfigType = {
    cameraConfig: any
    partRotation: any
    annotations: any
    patchConfigs: any
    transparent: any
    displayRawInspection: any
    displayPdfInspection: any
    dfmFeatureConfigs: any
    interactionMode: any
    crossSectionConfig: any
    customP2PPoints: any
    hoveredPoint: any
    selectedPoints: any
}

const DEFAULT_CONFIG: QMRConfigType = {
    cameraConfig: null,
    partRotation: null,
    annotations: [],
    patchConfigs: {},
    transparent: false,
    displayRawInspection: false,
    displayPdfInspection: null,
    dfmFeatureConfigs: [],
    interactionMode: null,
    crossSectionConfig: null,
    customP2PPoints: null,
    hoveredPoint: null,
    selectedPoints: null,
}

const createSimpleSelector = <Selected>(
    selector: (substate: ViewerStateType) => Selected,
) => {
    return createSelector((subState: ViewerStateType) => subState, selector)
}

/* Utility fxns */
interface DfmFeedbackInterface {
    id: string
    modelViewerSnapshots: { snapshot: DfmSnapshotType }[]
}
function getDfmModelViewerSnapshotJSON(dfm?: DfmFeedbackInterface) {
    const { modelViewerSnapshots = [] } = dfm || {}
    const dfmModelViewerSnapshotJSON =
        modelViewerSnapshots.length === 0
            ? null
            : modelViewerSnapshots[0].snapshot

    return dfmModelViewerSnapshotJSON || null
}

function getViewerConfigsFromDfmSnapshotJSON(
    dfmSnapshotJSON: DfmSnapshotType | null,
) {
    const {
        camera: cameraConfig = null,
        rotation: partRotation = null,
        annotations = null,
        part: { patches: patchConfigs = null, transparent = null } = {},
        dfm: dfmFeatureConfigs = null,
    } = dfmSnapshotJSON || {}

    return {
        cameraConfig,
        partRotation,
        annotations,
        patchConfigs,
        transparent,
        dfmFeatureConfigs,
    }
}

type MergedDfmConfig = {
    partRotation: null
    textureThreshold: number | null
    transparent: boolean
    annotations: AnnotationType[]
    patchConfigs: { [hashcode: string]: PatchConfigType }
    dfmFeatureConfigs: { [feature: string]: DfmFeatureConfigType }
    displayRawInspection: null
    displayPdfInspection: null
}
/* Utility selectors that don't depend directly on redux state */
const getMergedDfmViewerConfig = createSelector<
    DfmFeedbackInterface[],
    MergedDfmConfig,
    (DfmSnapshotType | null)[]
>(
    dfmFeedbackArr =>
        map(dfmFeedbackArr, dfmFeedback =>
            getDfmModelViewerSnapshotJSON(dfmFeedback),
        ),
    viewerSnapshotJSONs => {
        let mergedTransparency = false

        // In the case that we have multiple dfm feedback snapshots,
        // we're gonna have to take the last threshold for now. We can have a heirarchy later if we want,
        // but we can literally only ever show one texture at a time.
        let mergedTextureThreshold: number | null = null
        let mergedTextureDirection: number[] | null = null

        let mergedAnnotations: AnnotationType[] = []

        let mergedPatchConfigs: PatchConfigType[] = []

        let mergedDfmFeatureConfigs: DfmFeatureConfigType[] = []

        each(viewerSnapshotJSONs, viewerSnapshotJSON => {
            if (!viewerSnapshotJSON) {
                return
            }
            const {
                annotations,
                patchConfigs,
                transparent,
                dfmFeatureConfigs,
            } = getViewerConfigsFromDfmSnapshotJSON(viewerSnapshotJSON)

            const textureThreshold = dfmFeatureConfigs?.find(({ feature }) => {
                if (!feature) {
                    return
                }
                return TEXTURED_FEATURES.includes(feature)
            })?.threshold

            const textureDirection = dfmFeatureConfigs?.find(({ feature }) => {
                if (!feature) {
                    return
                }

                return TEXTURED_FEATURES_WITH_DIRECTION.includes(feature)
            })?.direction

            mergedTextureThreshold =
                mergedTextureThreshold || textureThreshold || null

            mergedTextureDirection =
                mergedTextureDirection || textureDirection || null

            mergedTransparency = !!(mergedTransparency || transparent)
            mergedAnnotations = annotations
                ? [...mergedAnnotations, ...annotations]
                : mergedAnnotations
            mergedPatchConfigs = patchConfigs
                ? [...mergedPatchConfigs, ...patchConfigs]
                : mergedPatchConfigs
            mergedDfmFeatureConfigs = dfmFeatureConfigs
                ? [...mergedDfmFeatureConfigs, ...dfmFeatureConfigs]
                : mergedDfmFeatureConfigs
        })

        return {
            partRotation: null,
            textureThreshold: mergedTextureThreshold,
            selectedPullDirectionArray: mergedTextureDirection,
            transparent: mergedTransparency,
            annotations: mergedAnnotations,
            patchConfigs: keyBy(mergedPatchConfigs, 'hashcode'),
            dfmFeatureConfigs: keyBy(mergedDfmFeatureConfigs, 'feature'),
            displayRawInspection: null,
            displayPdfInspection: null,
        }
    },
)

const _getCurrentCrossSectionFromViewer =
    createSimpleSelector<CrossSectionStateConfigType | null>(
        ({ viewer, interactionMode }) => {
            if (
                interactionMode !== CROSS_SECTION_MODE &&
                interactionMode !== CROSS_SECTION_ANNOTATION_MODE
            ) {
                return null
            }

            const crossSectionTools =
                viewer?.InteractionController?.crossSectionTools

            if (crossSectionTools == null) {
                return null
            }

            const position = crossSectionTools.getToolsPositionArray()
            const rotationMatrix = crossSectionTools.getToolsRotation()
            const quaternion = matrixToQuaternionArray(rotationMatrix)
            const toolsHidden = !crossSectionTools.toolsAreVisible()
            const enabled = crossSectionTools.crossSectionModeIsEnabled()

            return {
                mode: interactionMode,
                position,
                rotation: quaternion,
                toolsHidden,
                enabled,
                hoveredTool: null,
                view: null,
            }
        },
    )

const _getViewerInstance = createSimpleSelector(({ viewer }) => viewer)

const _getViewerInteractionMode = createSimpleSelector(
    ({ interactionMode }) => interactionMode,
)

const _getForceHideCrossSectionTools = createSimpleSelector(
    ({ forceHideCrossSectionTools }) => forceHideCrossSectionTools,
)

const _getCurrentCameraConfigFromViewer =
    createSimpleSelector<CameraConfigType | null>(({ viewer }) => {
        if (viewer == null) {
            return null
        }

        return {
            up: viewer.getCameraUpArray(),
            zoom: viewer.getCameraZoom(),
            target: viewer.getCameraTargetArray(),
            position: viewer.getCameraPositionArray(),
        }
    })

const getAnnotationForNewComment = (
    subState: ViewerStateType,
): CommentType | null => {
    const {
        viewer,
        interactionMode,
        selectedPatchConfigs,
        selectedPaletteColor,
        annotations,
    } = subState

    if (viewer == null) {
        return null
    }

    const shared = {
        id: NEW_COMMENT_ID,
        severity: 'comment',
        position: _getCurrentCameraConfigFromViewer(subState),
    }

    /*
        In order to unblock Admin quoting functionality, I'm making a compromise here.
        This "commenting" feature hasn't been in use in the last two years, so it feels silly to
        be bottlenecked by its limitation of only having one color to multiple patches.

        If the commenting feature comes back, maybe it can be moved to use the selector patterns
        that the admin quoting page uses.
    */
    if (interactionMode === SELECT_MODE) {
        return {
            ...shared,
            type: SURFACE_COMMENT,
            target: {
                patches: selectedPatchConfigs.map(({ hashcode }) => hashcode),
                color: selectedPaletteColor,
            },
        }
    }

    if (
        interactionMode === ANNOTATION_MODE ||
        interactionMode === CROSS_SECTION_ANNOTATION_MODE
    ) {
        // Only 1 annotation supported for "comments"
        const position = values(annotations)[0]?.position

        return {
            ...shared,
            type: PIN_COMMENT,
            target: { position },
        }
    }

    const crossSectionConfig = _getCurrentCrossSectionFromViewer(subState)

    if (interactionMode === CROSS_SECTION_MODE && crossSectionConfig) {
        const target = {
            position: crossSectionConfig.position,
            quaternion: crossSectionConfig.rotation,
        }

        return {
            ...shared,
            type: XSECTION_COMMENT,
            target,
        }
    }

    return null
}

const _getToolVisibility = createSimpleSelector(
    ({ toolVisibility }) => toolVisibility,
)

const getDfmById = createSelector<
    DfmFeedbackInterface[],
    { [dfmId: string]: DfmFeedbackInterface },
    { [dfmId: string]: DfmFeedbackInterface }
>(
    dfmFeedbackArr => keyBy(dfmFeedbackArr, 'id'),
    dfmFeedbackById => dfmFeedbackById,
)

const _getSelectedCommentId = createSimpleSelector(
    ({ selectedCommentId }) => selectedCommentId,
)

/*  Selectors that depend on local slice of redux state */
const _getHoveredDfmId = createSimpleSelector(
    ({ hoveredDfmId }) => hoveredDfmId,
)

const _getSelectedDfmId = createSimpleSelector(
    ({ selectedDfmId }) => selectedDfmId,
)

const _getSelectedPatchHashes = createSimpleSelector(
    ({ selectedPatchConfigs = [] }) =>
        selectedPatchConfigs.map(({ hashcode }) => hashcode),
)

const _getSelectedPatchConfigs = createSimpleSelector(
    ({ selectedPatchConfigs = [] }) => selectedPatchConfigs,
)

const _getSelectedPoints = createSimpleSelector(
    ({ selectedPoints = [] }) => selectedPoints,
)

const _getCustomP2PPoints = createSimpleSelector(
    ({ customP2PPoints }) => customP2PPoints,
)

const _getHoveredPoint = createSimpleSelector(
    ({ hoveredPoint }) => hoveredPoint,
)

const _getHoveredPatchHash = createSimpleSelector(
    ({ hoveredPatch }) => hoveredPatch,
)

const _getHoveredPatchHashes = createSimpleSelector(
    ({ hoveredPatches }) => hoveredPatches,
)

const _getHoveredHighlightPatchHash = createSimpleSelector(
    ({ hoveredHighlightPatchHash }) => hoveredHighlightPatchHash,
)

const _getTransparency = createSimpleSelector(({ transparent }) => transparent)

const _getDisplayRawInspection = createSimpleSelector(
    ({ displayRawInspection }) => displayRawInspection,
)

const _getDisplayPdfInspection = createSimpleSelector(
    ({ displayPdfInspection }) => displayPdfInspection,
)

const _getPartRotation = createSimpleSelector(
    ({ partRotation }) => partRotation,
)

const _getCameraConfig = createSimpleSelector(
    ({ cameraConfig }) => cameraConfig,
)

const _getAnnotations = createSimpleSelector(subState => {
    const { annotations = {} } = subState
    const interactionMode = _getViewerInteractionMode(subState)

    if (interactionMode !== TAPS_INSPECTION_MODE) {
        return values(annotations)
    }

    return []
})

const _getTextureThreshold = createSimpleSelector(
    ({ textureThreshold }) => textureThreshold || null,
)

const _getActiveDfmFeatures = createSimpleSelector(
    ({ activeDfmFeatures = [] }) => activeDfmFeatures,
)

const _getSelectAllDfmByDefaultFlag = createSimpleSelector(
    ({ selectAllDfmByDefault }) => selectAllDfmByDefault,
)

const _getMeasurementUnits = createSimpleSelector(({ units }) => units)

const _getAnnotatable = createSimpleSelector(({ annotatable }) => annotatable)

const _getSelectedPaletteColor = createSimpleSelector(
    ({ selectedPaletteColor }) => selectedPaletteColor || DEFAULT_COLOR,
)

const _getPaletteColors = createSimpleSelector(
    ({ paletteColors }) => paletteColors,
)

const _getSelectedPullDirection = createSimpleSelector(
    ({ selectedPullDirection }) => selectedPullDirection,
)

const _getSelectedPullDirectionArray = createSelector<
    ViewerStateType,
    TripleFloatType | null,
    PullDirectionType | null
>(
    ({ selectedPullDirection }) => selectedPullDirection,
    selectedPullDirection => {
        if (Array.isArray(selectedPullDirection)) {
            return selectedPullDirection
        }

        return (
            selectedPullDirection && PULL_DIRECTION_MAP[selectedPullDirection]
        )
    },
)

const _getCrossSectionConfig = createSimpleSelector(
    ({ interactionMode, crossSectionConfig, forceHideCrossSectionTools }) => {
        if (
            interactionMode === CROSS_SECTION_MODE ||
            interactionMode === CROSS_SECTION_ANNOTATION_MODE
        ) {
            const newCrossSectionConfig = {
                ...crossSectionConfig,
                enabled: true,
            }

            if (interactionMode === CROSS_SECTION_MODE) {
                newCrossSectionConfig.toolsHidden = forceHideCrossSectionTools
            }

            return newCrossSectionConfig
        }

        return {
            ...crossSectionConfig,
            enabled: false,
        }
    },
)

interface PatchInterface {
    id: string
}
interface HoleInterface {
    id: string
    groupKey: string
    hashcode: string
    patches: PatchInterface[]
    tapAnnotation: Record<string, any>
}
type HolesById = { [holeId: string]: HoleInterface }
type HolesByGroupKey = { [groupId: string]: HoleInterface[] }
type HolesByPatchHash = { [hashcode: string]: HoleInterface }

const _getOrganizedHoles = createSelector(
    (holes: HoleInterface[]) => holes,
    holes => {
        const holesByGroupKey: HolesByGroupKey = {}
        const holesById: HolesById = {}
        const holesByPatchHash: HolesByPatchHash = {}

        each(holes, hole => {
            holesById[hole.id] = hole
            if (!holesByGroupKey[hole.groupKey]) {
                holesByGroupKey[hole.groupKey] = []
            }
            /* Add hole for hole group key */
            holesByGroupKey[hole.groupKey].push(hole)

            /* Build map of hole patch hashes to hole */
            each(hole.patches, ({ id: patchHash }) => {
                holesByPatchHash[patchHash] = hole
            })
        })

        return {
            holesByGroupKey,
            holesById,
            holesByPatchHash,
        }
    },
)

const _getHoveredGroupHoles = createSelector(
    (subState: ViewerStateType) => subState,
    (
        _,
        args: {
            holesByGroupKey: HolesByGroupKey
            holes: HoleInterface[]
        },
    ) => args,
    ({ hoveredHoleGroupId }, { holesByGroupKey, holes }) => {
        if (!hoveredHoleGroupId) {
            return []
        }

        if (holesByGroupKey) {
            return holesByGroupKey[hoveredHoleGroupId] || []
        }

        if (holes) {
            const { holesByGroupKey: hByGroupKey } = _getOrganizedHoles(holes)

            return hByGroupKey[hoveredHoleGroupId] || []
        }

        return []
    },
)

const _getHoveredHole = createSelector(
    (subState: ViewerStateType) => subState,
    (
        _,
        args: {
            holesById: HolesById
            holes: HoleInterface[]
        },
    ) => args,
    ({ hoveredHoleId, hoveredTapsPatchHash }, { holesById, holes }) => {
        if (hoveredHoleId && holesById) {
            return holesById[hoveredHoleId] || null
        }

        if (holes) {
            const { holesById: hById, holesByPatchHash } =
                _getOrganizedHoles(holes)

            return (
                (hoveredHoleId && hById[hoveredHoleId]) ||
                (hoveredTapsPatchHash &&
                    holesByPatchHash[hoveredTapsPatchHash]) ||
                null
            )
        }

        return null
    },
)

const _getSelectedHole = createSelector(
    (subState: ViewerStateType) => subState,
    (
        _,
        args: {
            holes: HoleInterface[]
        },
    ) => args,
    ({ selectedTapsPatchHash }, { holes }) => {
        const { holesByPatchHash } = _getOrganizedHoles(holes)

        return (
            (selectedTapsPatchHash &&
                holesByPatchHash[selectedTapsPatchHash]) ||
            null
        )
    },
)

const _getHolesWithTapConfigured = createSelector(
    (_: ViewerStateType, args: { holes: HoleInterface[] }) => args,
    ({ holes }) => filter(holes || [], 'tapAnnotation'),
)

const _getHolePatchConfigs = createSelector(
    _getHoveredHole,
    _getHoveredGroupHoles,
    _getHolesWithTapConfigured,
    (hoveredHole, hoveredGroupHoles, holesWithTapConfigured) => {
        const hoveredHoleGroupPatchConfigs: {
            [patchId: string]: PatchConfigType
        } = {}

        each(hoveredGroupHoles, hole => {
            each(hole.patches, patch => {
                hoveredHoleGroupPatchConfigs[patch.id] = {
                    hashcode: patch.id,
                    color: hole.tapAnnotation ? DEFAULT_COLOR : ALERT_COLOR,
                }
            })
        })

        const configuredHolePatchConfigs: {
            [patchId: string]: PatchConfigType
        } = {}

        /* Only color tapped holes if user is not hovering over a hole group */
        if (isEmpty(hoveredGroupHoles)) {
            each(holesWithTapConfigured, ({ patches }) => {
                each(patches, ({ id: patchHash }) => {
                    configuredHolePatchConfigs[patchHash] = {
                        hashcode: patchHash,
                        color: DEFAULT_COLOR,
                    }
                })
            })
        }

        const hoveredHolePatchConfigs: {
            [patchId: string]: PatchConfigType
        } = {}

        each(hoveredHole?.patches || [], patch => {
            hoveredHolePatchConfigs[patch.id] = {
                hashcode: patch.id,
                color: ACTIVE_COLOR,
            }
        })
        return {
            ...hoveredHoleGroupPatchConfigs,
            ...configuredHolePatchConfigs,
            ...hoveredHolePatchConfigs,
        }
    },
)

const _getPatchConfigs = createSelector(
    _getViewerInteractionMode,
    _getSelectedPaletteColor,
    _getSelectedPatchConfigs,
    _getHoveredPatchHash,
    _getHoveredPatchHashes,
    _getHoveredHighlightPatchHash,
    (...args) => {
        const [
            viewerInteractionMode,
            selectedPaletteColor,
            selectedPatchConfigs,
            hoveredPatchHash,
            hoveredPatchHashes,
            hoveredHighlightPatchHash,
        ] = args
        const patchConfigs: {
            [hashcode: string]: PatchConfigType
        } = {}
        const color = selectedPaletteColor

        if (viewerInteractionMode !== TAPS_INSPECTION_MODE) {
            selectedPatchConfigs.forEach(config => {
                patchConfigs[config.hashcode] = config
            })

            if (hoveredHighlightPatchHash) {
                const hashcode = hoveredHighlightPatchHash

                patchConfigs[hashcode] = {
                    hashcode,
                    highlight: true,
                }
            }

            if (hoveredPatchHash != null) {
                const hashcode = hoveredPatchHash

                patchConfigs[hashcode] = {
                    hashcode,
                    color,
                }
            }

            if (hoveredPatchHashes) {
                hoveredPatchHashes.forEach(hashcode => {
                    patchConfigs[hashcode] = {
                        hashcode,
                        color,
                    }
                })
            }
        }

        return patchConfigs
    },
)

const _getDfmFeatureConfigs = createSelector(
    _getTextureThreshold,
    _getSelectedPullDirectionArray,
    _getActiveDfmFeatures,
    (textureThreshold, selectedPullDirectionArray, activeDfmFeatures) => {
        const dfmFeatureConfigs = reduce(
            activeDfmFeatures,
            (
                result: {
                    [feature: string]: {
                        feature: string
                        threshold: number
                        direction: number[] | null
                    }
                },
                feature,
            ) => {
                result[feature] = {
                    feature,
                    threshold: textureThreshold || 0,
                    direction: selectedPullDirectionArray || null,
                }
                return result
            },
            {},
        )

        return dfmFeatureConfigs
    },
)

const _getConfigForTapMode = createSelector(
    subState => subState,
    _getHolePatchConfigs,
    (subState, holePatchConfigs): QMRConfigType => {
        return {
            cameraConfig: _getCameraConfig(subState),
            partRotation: null,
            annotations: [],
            patchConfigs: holePatchConfigs,
            transparent: _getTransparency(subState),
            displayRawInspection: _getDisplayRawInspection(subState),
            displayPdfInspection: _getDisplayPdfInspection(subState),
            dfmFeatureConfigs: _getDfmFeatureConfigs(subState),
            crossSectionConfig: _getCrossSectionConfig(subState),
            interactionMode: TAPS_INSPECTION_MODE,
            customP2PPoints: _getCustomP2PPoints(subState),
            hoveredPoint: _getHoveredPoint(subState),
            selectedPoints: _getSelectedPoints(subState),
        }
    },
)

type ConfigFromDfmIdArgs = {
    dfmId: string
    dfmFeedbackArr: DfmFeedbackInterface[]
}

const getConfigFromDfmId = createSelector(
    (_, { dfmId }: ConfigFromDfmIdArgs) => dfmId,
    (_, { dfmFeedbackArr }: ConfigFromDfmIdArgs) => getDfmById(dfmFeedbackArr),
    (subState: ViewerStateType) => _getCrossSectionConfig(subState),
    (subState: ViewerStateType) => _getTextureThreshold(subState),
    (subState: ViewerStateType) => _getHoveredPoint(subState),
    (...args) => {
        const [
            dfmId,
            dfmFeedbackById,
            crossSectionConfig,
            textureThreshold,
            hoveredPoint,
        ] = args

        const dfm = dfmFeedbackById[dfmId]
        const dfmModelViewerSnapshotJSON = getDfmModelViewerSnapshotJSON(dfm)
        const {
            camera: cameraConfig = null,
            rotation: partRotation = null,
            annotations = [],
            part: { patches: patchConfigs = [], transparent = false } = {},
            dfm: snapshotDfmFeatureConfigs = [],
            crossSection,
            pointToPoint,
        } = dfmModelViewerSnapshotJSON || {}

        const dfmFeatureConfigs =
            snapshotDfmFeatureConfigs?.reduce(
                (result, featureConfig) => {
                    const { feature, threshold, direction } = featureConfig

                    if (!feature) {
                        return result
                    }

                    result[feature] = {
                        feature,
                        threshold:
                            (TEXTURED_FEATURES_WITH_THRESHOLD.includes(
                                feature,
                            ) &&
                                threshold) ||
                            0,
                        direction:
                            (TEXTURED_FEATURES_WITH_DIRECTION.includes(
                                feature,
                            ) &&
                                direction) ||
                            null,
                    }

                    return result
                },
                {} as {
                    [key: string]: {
                        feature: string
                        threshold?: number | null
                        direction?: number[] | null
                    }
                },
            ) || {}

        const config = {
            cameraConfig,
            partRotation,
            annotations,
            patchConfigs: keyBy(patchConfigs, 'hashcode'),
            transparent,
            displayRawInspection: false,
            displayPdfInspection: false,
            interactionMode: SELECT_MODE,
            dfmFeatureConfigs,
            textureThreshold,
            crossSectionConfig: {
                ...crossSectionConfig,
                crossSectionPosition: null,
                crossSectionRotation: null,
            } as CrossSectionStateConfigType & {
                crossSectionRotation?: THREE.Matrix4 | null
                crossSectionPosition?: TripleFloatType | null
            },
            selectedPoints: pointToPoint?.selectedPoints || [],
            customP2PPoints: pointToPoint?.customPoints || [],
            hoveredPoint,
        }

        if (crossSection) {
            config.interactionMode = CROSS_SECTION_MODE
            config.crossSectionConfig = {
                ...crossSectionConfig,
                enabled: true,
                toolsHidden: true,
                crossSectionRotation: quaternionToMatrix(crossSection.rotation),
                crossSectionPosition: crossSection.position,
            }
        } else {
            config.crossSectionConfig = {
                ...crossSectionConfig,
                enabled: false,
            }
        }

        return config
    },
)

const _getConfigFromState = createSelector(
    _getPatchConfigs,
    _getAnnotations,
    _getTransparency,
    _getDisplayRawInspection,
    _getDisplayPdfInspection,
    _getCameraConfig,
    _getPartRotation,
    _getDfmFeatureConfigs,
    _getViewerInteractionMode,
    _getCrossSectionConfig,
    _getTextureThreshold,
    _getSelectedPoints,
    _getHoveredPoint,
    _getCustomP2PPoints,
    (...args) => {
        const [
            patchConfigs,
            annotations,
            transparent,
            displayRawInspection,
            displayPdfInspection,
            cameraConfig,
            partRotation,
            dfmFeatureConfigs,
            interactionMode,
            crossSectionConfig,
            textureThreshold,
            selectedPoints,
            hoveredPoint,
            customP2PPoints,
        ] = args

        return {
            cameraConfig,
            partRotation,
            annotations,
            patchConfigs,
            transparent,
            displayRawInspection,
            displayPdfInspection,
            dfmFeatureConfigs,
            interactionMode,
            crossSectionConfig,
            textureThreshold,
            selectedPoints,
            hoveredPoint,
            customP2PPoints,
        }
    },
)

const _getShouldSelectAllDfm = createSimpleSelector(subState => {
    const activeDfmId = _getSelectedDfmId(subState)
    const selectAllDfmByDefault = _getSelectAllDfmByDefaultFlag(subState)
    const annotatable = _getAnnotatable(subState)

    let noExistingAnnotations = true

    if (annotatable) {
        const selectedPatchConfigs = _getSelectedPatchConfigs(subState)
        const annotations = _getAnnotations(subState)

        noExistingAnnotations =
            annotations.length === 0 && selectedPatchConfigs.length === 0
    }

    return !activeDfmId && noExistingAnnotations && selectAllDfmByDefault
})

const makePatchConfigs = ({
    color,
    patches,
}: {
    color: string
    patches: string[]
}) => {
    return keyBy(
        map(patches, hashcode => {
            return { hashcode, color }
        }),
        'hashcode',
    )
}

type ConfigFromCommentArgs = {
    activeCommentId: string
    annotations: CommentType[]
}
const _getConfigFromComment = createSelector(
    (subState: ViewerStateType) => subState,
    (_, { annotations }: ConfigFromCommentArgs) => annotations,
    (_, { activeCommentId }: ConfigFromCommentArgs) => activeCommentId,
    (subState, annotations, activeCommentId) => {
        const { crossSectionConfig } = subState

        let comment: CommentType | null | undefined = null
        const isNewComment = activeCommentId === NEW_COMMENT_ID

        if (isNewComment) {
            comment = getAnnotationForNewComment(subState)
        }

        if (comment == null) {
            comment = find(annotations, an => an.id === activeCommentId)
        }

        if (comment == null) {
            return DEFAULT_CONFIG
        }

        switch (comment.type) {
            case SURFACE_COMMENT: {
                return {
                    ...DEFAULT_CONFIG,
                    cameraConfig: comment.position,
                    patchConfigs: makePatchConfigs(comment.target),
                    interactionMode: SELECT_MODE,
                    selectedPaletteColor: comment.target.color,
                    crossSectionConfig: {
                        ...crossSectionConfig,
                        enabled: false,
                    },
                }
            }
            case PIN_COMMENT: {
                return {
                    ...DEFAULT_CONFIG,
                    cameraConfig: comment.position,
                    interactionMode: ANNOTATION_MODE,
                    crossSectionConfig: {
                        ...crossSectionConfig,
                        enabled: false,
                    },
                    annotations: [
                        {
                            position: comment.target.position,
                            metadata: {
                                id: comment.id,
                            },
                        },
                    ],
                }
            }
            case XSECTION_COMMENT: {
                return {
                    ...DEFAULT_CONFIG,
                    cameraConfig: comment.position,
                    interactionMode: CROSS_SECTION_MODE,
                    crossSectionConfig: {
                        ...crossSectionConfig,
                        enabled: true,
                        toolsHidden: !isNewComment,
                        crossSectionPosition: comment.target.position,
                        crossSectionRotation:
                            comment.target.quaternion &&
                            quaternionToMatrix(comment.target.quaternion),
                    },
                }
            }
            default:
                return DEFAULT_CONFIG
        }
    },
)

const _getModelViewerConfig = createSelector(
    subState => subState,
    _getViewerInteractionMode,
    _getAnnotatable,
    (_, args) => args,
    (subState, interactionMode, annotatable, args) => {
        const { dfmFeedbackArr, comments } = args

        if (interactionMode === TAPS_INSPECTION_MODE) {
            return _getConfigForTapMode(subState, args)
        }

        const activeCommentId = _getSelectedCommentId(subState)

        if (activeCommentId && comments != null && annotatable) {
            return _getConfigFromComment(subState, {
                activeCommentId,
                comments,
            })
        }

        const activeDfmId = _getSelectedDfmId(subState)

        if (activeDfmId) {
            return getConfigFromDfmId(subState, {
                dfmId: activeDfmId,
                dfmFeedbackArr,
            })
        }

        const shouldMergeAllDfm = _getShouldSelectAllDfm(subState)

        if (shouldMergeAllDfm) {
            return {
                ..._getConfigFromState(subState),
                ...getMergedDfmViewerConfig(dfmFeedbackArr),
            }
        }

        return _getConfigFromState(subState)
    },
)

const _anyCommentsOrDfmSelected = createSelector(
    _getSelectedCommentId,
    _getSelectedDfmId,
    (selectedCommentId, selectedDfmId) => {
        return Boolean(selectedCommentId) || Boolean(selectedDfmId)
    },
)

/* Utility fxn to transform selectors that depend on a slice of redux state into */
/* exportable selectors that depend on global redux state */
function wrapSelector<
    Selector extends (state: ViewerStateType, ...args: any) => any,
>(
    selectorFxn: Selector,
): (
    globalState: Record<string, ViewerStateType>,
    ...args: any
) => ReturnType<Selector> {
    return (globalState: Record<string, ViewerStateType>, ...args: any) => {
        const moduleState = globalState[NAMESPACE] || DEFAULT_STATE

        return selectorFxn(moduleState, ...args)
    }
}

export const getToolVisibility = wrapSelector(_getToolVisibility)

export const getSelectAllDfmByDefaultFlag = wrapSelector(
    _getSelectAllDfmByDefaultFlag,
)
export const getSelectedPoints = wrapSelector(_getSelectedPoints)
export const getCustomP2PPoints = wrapSelector(_getCustomP2PPoints)
export const getSelectedPatchHashes = wrapSelector(_getSelectedPatchHashes)
export const getSelectedPatchConfigs = wrapSelector(_getSelectedPatchConfigs)
export const getMeasurementUnits = wrapSelector(_getMeasurementUnits)
export const getShouldSelectAllDfm = wrapSelector(_getShouldSelectAllDfm)
export const getViewerInteractionMode = wrapSelector(_getViewerInteractionMode)
export const getForceHideCrossSectionTools = wrapSelector(
    _getForceHideCrossSectionTools,
)
export const getTransparency = wrapSelector(_getTransparency)
export const getAnnotations = wrapSelector(_getAnnotations)
export const getTextureThreshold = wrapSelector(_getTextureThreshold)
export const getSelectedDfmId = wrapSelector(_getSelectedDfmId)
export const getHoveredDfmId = wrapSelector(_getHoveredDfmId)
export const getAnnotatable = wrapSelector(_getAnnotatable)
export const getSelectedPaletteColor = wrapSelector(_getSelectedPaletteColor)
export const getPaletteColors = wrapSelector(_getPaletteColors)
export const getSelectedPullDirection = wrapSelector(_getSelectedPullDirection)
export const getSelectedPullDirectionArray = wrapSelector(
    _getSelectedPullDirectionArray,
)
export const getCrossSectionConfig = wrapSelector(_getCrossSectionConfig)
export const getActiveDfmFeatures = wrapSelector(_getActiveDfmFeatures)
export const anyCommentsOrDfmSelected = wrapSelector(_anyCommentsOrDfmSelected)
export const getHoveredHole = wrapSelector(_getHoveredHole)
export const getSelectedHole = wrapSelector(_getSelectedHole)
export const getModelViewerConfig = wrapSelector(_getModelViewerConfig)
export const getPartRotation = wrapSelector(_getPartRotation)

// Get information from the viewer:
// The other getters are useful for controlling the viewer (ie. when you update them, then the viewer will update)
// These are good for getting state from the viewer that you want to save. (eg. saving DFM)
export const getViewerInstance = wrapSelector(_getViewerInstance)
export const getCurrentCameraConfigFromViewer = wrapSelector(
    _getCurrentCameraConfigFromViewer,
)
export const getCurrentCrossSectionFromViewer = wrapSelector(
    _getCurrentCrossSectionFromViewer,
)
